import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Upload, X, Loader2, Check, Trash2 } from 'lucide-react';
import { uploadFile, dbUpdate } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

// Default avatar path
export const DEFAULT_AVATAR = '/assets/avatar.svg';

interface ProfilePictureUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPhotoURL?: string | null;
  onSuccess?: (newPhotoURL: string) => void;
}

export const ProfilePictureUploadModal: React.FC<ProfilePictureUploadModalProps> = ({
  isOpen,
  onClose,
  currentPhotoURL,
  onSuccess
}) => {
  const { user } = useAuth();
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setError(null);
    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !user) return;

    setUploading(true);
    setError(null);

    try {
      // Upload to Firebase Storage
      const ext = file.name.split('.').pop();
      const storagePath = `profile-pictures/${user.uid}/avatar.${ext}`;
      const downloadUrl = await uploadFile(storagePath, file);

      // Update user profile in database
      await dbUpdate(`v1/users/profiles/${user.uid}`, {
        photoURL: downloadUrl,
        updatedAt: Date.now()
      });

      setSuccess(true);
      onSuccess?.(downloadUrl);

      // Close after a short delay
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!user) return;

    setUploading(true);
    setError(null);

    try {
      // Set to default avatar
      await dbUpdate(`v1/users/profiles/${user.uid}`, {
        photoURL: DEFAULT_AVATAR,
        updatedAt: Date.now()
      });

      setSuccess(true);
      onSuccess?.(DEFAULT_AVATAR);

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error('Remove failed:', err);
      setError(err.message || 'Failed to remove photo');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    setFile(null);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const displayPhoto = preview || currentPhotoURL || DEFAULT_AVATAR;
  const hasCustomPhoto = currentPhotoURL && currentPhotoURL !== DEFAULT_AVATAR;

  if (!isOpen) return null;

  // Use portal to render outside the header's stacking context
  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-sm bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-primary/30 rounded-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-gray-500 dark:text-white/60 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors z-10"
          >
            <X size={20} />
          </button>

          {/* Content */}
          <div className="p-6">
            {/* Title */}
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
              Profile Picture
            </h2>
            <p className="text-gray-500 dark:text-white/60 text-center text-sm mb-6">
              Upload a new photo or remove your current one
            </p>

            {/* Preview */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <img
                  src={displayPhoto}
                  alt="Profile preview"
                  className="w-32 h-32 rounded-full object-cover ring-4 ring-primary/20"
                />
                {preview && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center"
                  >
                    <Check size={16} className="text-black" />
                  </motion.div>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm text-center"
              >
                {error}
              </motion.div>
            )}

            {/* Success */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-xl text-green-500 text-sm text-center flex items-center justify-center gap-2"
              >
                <Check size={16} />
                Photo updated successfully!
              </motion.div>
            )}

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
            />

            {/* Buttons */}
            <div className="space-y-3">
              {preview ? (
                <>
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="w-full py-3 bg-primary text-black font-bold rounded-full hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        Save Photo
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setPreview(null);
                      setFile(null);
                    }}
                    disabled={uploading}
                    className="w-full py-3 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white font-medium rounded-full hover:bg-gray-200 dark:hover:bg-white/20 transition-colors disabled:opacity-50"
                  >
                    Choose Different Photo
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full py-3 bg-primary text-black font-bold rounded-full hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Camera size={18} />
                    Choose Photo
                  </button>
                  {hasCustomPhoto && (
                    <button
                      onClick={handleRemovePhoto}
                      disabled={uploading}
                      className="w-full py-3 bg-red-500/10 text-red-500 font-medium rounded-full hover:bg-red-500/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {uploading ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                      Remove Photo
                    </button>
                  )}
                </>
              )}
            </div>

            <p className="text-center text-gray-400 dark:text-white/40 text-xs mt-4">
              Recommended: Square image, at least 200x200px
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ProfilePictureUploadModal;
